
import { useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import type { Flashcard, FlashcardDeck } from '@/types/flashcard';
import { transformDatabaseFlashcard, transformDatabaseFlashcardDeck } from '@/lib/flashcard-utils';

// Spaced repetition intervals in days based on confidence level and repetition count
const getReviewInterval = (confidence: number, repetitionCount: number): number => {
  // Base intervals (in days)
  const baseIntervals = [1, 3, 7, 14, 21, 30, 60, 90, 180];
  
  // Calculate interval index based on confidence and repetition
  let intervalIndex = Math.min(
    Math.floor(confidence * 3) + Math.min(repetitionCount, 5),
    baseIntervals.length - 1
  );
  
  // For very low confidence, always use the shortest interval
  if (confidence < 0.2) intervalIndex = 0;
  
  return baseIntervals[intervalIndex];
};

export function useSpacedRepetitionScheduler() {
  // Schedule next review for a flashcard based on user's confidence rating
  const scheduleNextReview = useCallback(async (
    cardId: string,
    confidence: number // 0 to 1, with 1 being highest confidence
  ) => {
    try {
      if (!cardId) {
        console.error('Invalid card ID provided to scheduleNextReview');
        return false;
      }

      // Get current card data
      const { data: card, error: cardError } = await supabase
        .from('flashcards')
        .select('repetition_count')
        .eq('id', cardId)
        .single();
        
      if (cardError) {
        console.error('Error fetching card data:', cardError);
        throw cardError;
      }
      
      const repetitionCount = (card?.repetition_count || 0) + 1;
      const intervalDays = getReviewInterval(confidence, repetitionCount);
      
      // Calculate next review date
      const nextReview = new Date();
      nextReview.setDate(nextReview.getDate() + intervalDays);
      
      // Update the flashcard
      const { error } = await supabase
        .from('flashcards')
        .update({
          confidence,
          repetition_count: repetitionCount,
          last_reviewed: new Date().toISOString(),
          next_review: nextReview.toISOString(),
          // Adjust difficulty based on confidence
          difficulty: confidence < 0.3 ? 'hard' : confidence > 0.7 ? 'easy' : 'medium'
        })
        .eq('id', cardId);
        
      if (error) {
        console.error('Error updating card data:', error);
        throw error;
      }
      
      console.log(`Card scheduled for review in ${intervalDays} days`);
      return true;
    } catch (error) {
      console.error('Error scheduling next flashcard review:', error);
      toast.error('Failed to schedule next review');
      return false;
    }
  }, []);
  
  // Get due flashcards that need review today
  const getDueFlashcards = useCallback(async (deckId?: string): Promise<Flashcard[]> => {
    try {
      const today = new Date().toISOString();
      console.log('Fetching flashcards due for review before:', today);
      
      let query = supabase
        .from('flashcards')
        .select('*')
        .or(`next_review.lt.${today},next_review.is.null`);
        
      if (deckId) {
        query = query.eq('deck_id', deckId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error in getDueFlashcards:', error);
        throw error;
      }
      
      console.log(`Found ${data?.length || 0} due flashcards`);
      return (data?.map(card => transformDatabaseFlashcard(card)) || []) as Flashcard[];
    } catch (error) {
      console.error('Error fetching due flashcards:', error);
      return [];
    }
  }, []);

  // Get all flashcards from mistake decks
  const getMistakeFlashcards = useCallback(async (): Promise<Flashcard[]> => {
    try {
      console.log('Searching for quiz mistake flashcards...');
      
      // Approach 1: Find all cards marked as quiz mistakes by source
      const { data: mistakeCards, error: mistakeError } = await supabase
        .from('flashcards')
        .select('*')
        .eq('source', 'quiz_mistake');
        
      if (mistakeError) {
        console.error('Error fetching mistake cards by source:', mistakeError);
        // Fall back to approach 2 if this fails
      } else if (mistakeCards && mistakeCards.length > 0) {
        console.log(`Found ${mistakeCards.length} flashcards tagged as quiz mistakes`);
        return (mistakeCards.map(card => transformDatabaseFlashcard(card)) || []) as Flashcard[];
      }
      
      // Approach 2: Find all decks with "Quiz Mistakes" in the name
      console.log('Falling back to searching by deck name...');
      const { data: decks, error: deckError } = await supabase
        .from('flashcard_decks')
        .select('id, name')
        .ilike('name', '%Quiz Mistakes%');
        
      if (deckError) {
        console.error('Error fetching mistake decks:', deckError);
        throw deckError;
      }
      
      if (!decks || decks.length === 0) {
        console.log('No mistake decks found');
        return [];
      }
      
      const deckIds = decks.map(deck => deck.id);
      console.log('Found mistake decks:', decks.map(d => d.name));
      console.log('Deck IDs:', deckIds);
      
      // Then get all cards from these decks
      const { data, error } = await supabase
        .from('flashcards')
        .select('*')
        .in('deck_id', deckIds);
        
      if (error) {
        console.error('Error fetching mistake flashcards:', error);
        throw error;
      }
      
      console.log(`Found ${data?.length || 0} flashcards from mistake decks`);
      return (data?.map(card => transformDatabaseFlashcard(card)) || []) as Flashcard[];
    } catch (error) {
      console.error('Error fetching mistake flashcards:', error);
      return [];
    }
  }, []);

  // Get a specific mistake deck by ID or create one if it doesn't exist
  const getOrCreateMistakeDeck = useCallback(async (
    subject: string,
    category: string
  ): Promise<FlashcardDeck | null> => {
    try {
      const deckName = `Quiz Mistakes - ${subject}`;
      console.log(`Looking for deck with name: "${deckName}"`);
      
      // First, try to find if any decks exist with this exact name
      const { data: existingDecks, error: findError } = await supabase
        .from('flashcard_decks')
        .select('*')
        .eq('name', deckName);
        
      if (findError) {
        console.error('Error finding mistake decks:', findError);
        throw findError;
      }
      
      // If we found multiple decks with the same name, use the first one
      if (existingDecks && existingDecks.length > 0) {
        console.log(`Found ${existingDecks.length} existing mistake decks, using the first one:`, existingDecks[0]);
        return transformDatabaseFlashcardDeck(existingDecks[0]);
      }
      
      // Create a new deck if none exists
      console.log('Creating new mistake deck for subject:', subject);
      const { data: newDeck, error: createError } = await supabase
        .from('flashcard_decks')
        .insert({
          name: deckName,
          description: `Flashcards created from your quiz mistakes in ${subject}`,
          subject,
          category,
          card_count: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select('*')
        .single();
        
      if (createError) {
        console.error('Error creating mistake deck:', createError);
        throw createError;
      }
      
      console.log('Created new mistake deck:', newDeck);
      return transformDatabaseFlashcardDeck(newDeck);
    } catch (error) {
      console.error('Error in getOrCreateMistakeDeck:', error);
      toast.error('Failed to create flashcard deck for mistakes');
      return null;
    }
  }, []);

  return {
    scheduleNextReview,
    getDueFlashcards,
    getMistakeFlashcards,
    getOrCreateMistakeDeck,
    getReviewInterval
  };
}
